var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, LitElement } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import styles from './file-preview.styles';
import { dictionary, formatFileSize } from '@mf-cms/helpers';
import { classMap } from 'lit/directives/class-map.js';
import { getCreativeCommonsDictionary, getLicenseDictionary, getLicenseModelDictionary } from '@mf-cms/helpers';
import '@mf-cms/components/src/tag/tag';
import '@mf-cms/components/src/mark/mark';
let PFilePreview = class PFilePreview extends LitElement {
    constructor() {
        super(...arguments);
        this.culture = 'en-GB';
        this.fields = [];
        this.currentIndex = 0;
        this.nextIndex = 0;
        this.prevIndex = 0;
        this.previewImage = new Image();
        this.formattedFields = [];
        this.showLicenseInfo = false;
        this.showModelInfo = false;
        this.fileLoading = false;
        this.previewLoading = false;
        /**
         * Formats and processes the fields for display
         *
         * @async
         * @returns {Promise<Array<{
         *   id: number,
         *   value: any,
         *   isCustomField: boolean,
         *   title: string,
         *   sortOrder: number
         * }>>} An array of formatted field objects, sorted by sortOrder
         *
         * @description
         * This method processes the fields array by:
         * 1. Mapping each field to include its value and metadata
         * 2. Filtering out fields with no values
         * 3. Sorting the results by sortOrder
         */
        this.getFormattedFields = async () => {
            if (this.fields.length <= 0)
                return [];
            const result = await Promise.all(this.fields.map(async (field) => {
                const value = {
                    id: field.id,
                    value: await this.getFieldValue(field.id, this.file, field.isCustomField),
                    isCustomField: field.isCustomField,
                    title: field.title,
                    sortOrder: field.sortOrder
                };
                if (value.value) {
                    return value;
                }
            }));
            return result.filter((x) => x != undefined).sort((a, b) => a.sortOrder - b.sortOrder);
        };
    }
    static { this.styles = styles; }
    connectedCallback() {
        super.connectedCallback();
        document.addEventListener('keydown', this.handleKeyDown.bind(this));
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('keydown', this.handleKeyDown.bind(this));
    }
    /**
     * Show dialog and load file
     * @param {string} fileId - File id
     * @param {string[]} allFileIds - All file ids that should be included in the stepper
     * @returns {void}
     */
    async open(fileId, allFileIds) {
        if (!this.isOpen) {
            setTimeout(() => {
                this.dialog.classList.add('open');
            }, 1);
        }
        this.isOpen = true;
        this.allFileIds = allFileIds;
        // Clear componenten from slotted video
        this.innerHTML = '';
        // Reset formatted fields
        this.formattedFields = [];
        // Reset image
        this.previewImage = new Image();
        // Reset loading states
        this.fileLoading = true;
        this.previewLoading = true;
        // Open the dialog
        this.dialog.open();
        // Set indexes for the stepper
        this.currentIndex = this.allFileIds.indexOf(fileId) + 1;
        this.nextIndex = this.currentIndex + 1;
        this.prevIndex = this.currentIndex - 1;
        // Get the file and permissions
        this.file = await this.getFile(fileId);
        this.permissions = await this.getPermissions(fileId);
        if (!this.file) {
            this.handleNoFile();
            return;
        }
        this.showLicenseInfo = !!this.fields.find((field) => field.id == 60);
        this.showModelInfo = !!this.fields.find((field) => field.id == 61);
        this.formattedFields = await this.getFormattedFields();
        // Remove file loading state
        this.fileLoading = false;
        // Show the correct thing on the stage based on the file type
        if (this.file.type.type == 'video' || this.file.type.type == 'sound') {
            // Video || Audio
            this.viewMode = 'video';
            this.imageContainerElm.innerHTML = '';
            // Create div with id for the video player
            // Append video to stage slot
            // Call global initVideo()
            const videoElement = this.createOrGetVideoElement();
            window.initVideo(videoElement);
        }
        else if (this.hasDocumentPreview(parseInt(this.file.type.id))) {
            // Document
            this.viewMode = 'document';
            this.requestUpdate();
        }
        else if (this.file.thumbPreview !== null) {
            // Image
            this.viewMode = 'image';
            this.previewImage.src = await this.getLargePreview(fileId);
            this.previewImage.alt = this.file?.alttext;
            await this.previewImage.decode();
            // Append image to stage
            this.imageContainerElm.innerHTML = '';
            this.imageContainerElm.appendChild(this.previewImage);
        }
        else {
            // Icon (fallback)
            this.viewMode = 'icon';
            this.imageContainerElm.innerHTML = '';
            const icon = new Image();
            icon.src = this.getFileIconPath(parseInt(this.file.type.id));
            icon.alt = this.file?.alttext;
            this.imageContainerElm.appendChild(icon);
        }
        this.previewLoading = false;
    }
    /**
     * Creates or gets a video element for the file preview
     * @returns {HTMLElement} The video element container div
     *
     * @description
     * This method handles creating or retrieving the video player container element:
     * 1. Checks if a video element already exists for this media ID
     * 2. If it exists, removes it to prevent duplicates
     * 3. Creates a new div element configured for the media player
     * 4. Sets appropriate data attributes and classes
     * 5. Appends it to the component
     */
    createOrGetVideoElement() {
        const selector = `p-file-preview div[data-id="${this.file.mediaId}"].media-player`;
        let videoElement = document.querySelector(selector);
        if (videoElement)
            videoElement.remove();
        videoElement = document.createElement('div');
        videoElement.dataset.id = this.file.mediaId;
        videoElement.dataset.type = this.file.type.type == 'sound' ? 'audio' : 'video';
        videoElement.classList.add('media-player');
        videoElement.setAttribute('slot', 'video');
        this.appendChild(videoElement);
        return videoElement;
    }
    /**
     * Handle case when no file is found
     * @todo Add proper error handling and user feedback
     * @returns {void}
     */
    handleNoFile() { }
    /**
     * Get file
     * @param {string} fileId - File id
     * @returns {Array} File
     */
    async getFile(fileId) {
        this.accessToken = window.getAccessToken();
        try {
            // TODO: Add fields to the query
            const response = await fetch(`${process.env.API_URL}/file/${fileId}/?fields=any&access_token=${this.accessToken}`);
            if (response.status === 404) {
                return [];
            }
            const json = await response.json();
            return json[0];
        }
        catch (e) {
            console.error(e);
        }
        return [];
    }
    /**
     * Get permissions
     * @param {string} fileId - File id
     * @returns {Array} Permissions
     */
    async getPermissions(fileId) {
        this.accessToken = window.getAccessToken();
        try {
            const response = await fetch(`${process.env.API_URL}/file/${fileId}/checkpermissions?cache=0&access_token=${this.accessToken}`);
            if (response.status === 404) {
                return [];
            }
            const json = await response.json();
            return json[0];
        }
        catch (e) {
            console.error(e);
        }
        return [];
    }
    /**
     * Get large preview
     * @param {string} fileId - File id
     * @returns {string} Preview url
     */
    async getLargePreview(fileId) {
        try {
            const response = await fetch(`${process.env.API_URL}/file/${fileId}/dynpreview?access_token=${this.accessToken}&format=webp`);
            if (response.status === 404) {
                return '';
            }
            const json = await response.json();
            return json[0].downloadURL;
        }
        catch (e) {
            console.error(e);
        }
    }
    /**
     * Get license data
     * @param {Rights} rights rights
     * @param {string} culture culture
     * @returns {string | TemplateResult} license data string
     */
    async getLicenseData(rights, culture) {
        if (!rights || !(rights.licenseInformation || rights.rightsInformation || rights.rightsType)) {
            return '';
        }
        return html ` ${rights.rightsType ? html `
            <strong>${dictionary(culture, 'filePreview', 'licenceTypeHeading')}</strong>
            <div class="rights-info">
              ${await getLicenseDictionary(dictionary, culture, rights.rightsType)}
            </div>
            ${rights.rightsType == 'LIMITED' && (rights.useFrom || rights.useTo)
            ? html ` <strong>${dictionary(culture, 'filePreview', 'validUntil')}</strong> ${rights.useFrom && rights.useTo ? html `
                        <div class="rights-info">
                          ${dictionary(culture, 'filePreview', 'licenseUseToFrom', [
                new Date(rights.useFrom).format(),
                new Date(rights.useTo).format()
            ])}
                        </div>
                      `
                : ''}
                  ${rights.useFrom && !rights.useTo
                ? html ` <div class="rights-info"> ${dictionary(culture, 'filePreview', 'from')} ${new Date(rights.useFrom).format()} </div> `
                : ''}
                  ${!rights.useFrom && rights.useTo
                ? html ` <div class="rights-info"> ${dictionary(culture, 'filePreview', 'until')} ${new Date(rights.useTo).format()} </div> `
                : ''}
                `
            : ''}
          `
            : ``}
      ${rights.rightsInformation
            ? html ` <strong>${dictionary(culture, 'filePreview', 'rightsInformationHeading')}</strong> <div class="rights-info"> ${rights.rightsType == 'CC' ? await getCreativeCommonsDictionary(dictionary, culture, rights.rightsInformation) : rights.rightsInformation} </div> `
            : ``}
      ${rights.licenseInformation
            ? html ` <strong>${dictionary(culture, 'filePreview', 'licenseInformationHeading')}</strong> <div class="rights-info">${rights.licenseInformation}</div> `
            : ``}
    `;
    }
    /**
     * @param {Rights} rights rights
     * @param {string} culture culture
     * @returns {string | TemplateResult} license data string
     */
    async getModelData(rights, culture) {
        if (!rights ||
            !(rights.contactCity ||
                rights.contactEmail ||
                rights.contactPhone ||
                rights.modelInformation ||
                rights.modelPermission ||
                rights.modelRestrictions)) {
            return '';
        }
        return html ` ${rights.modelPermission ? html `
        <strong>${dictionary(culture, 'filePreview', 'modelPermissionsHeading')}</strong>
        <div class="rights-info">${await getLicenseModelDictionary(dictionary, culture, rights.modelPermission)}</strong>
      `
            : ``}
      ${rights.modelInformation
            ? html ` <strong>${dictionary(culture, 'filePreview', 'modelInformationHeading')}</strong> <div class="rights-info">${rights.modelInformation}</div> `
            : ``}
      ${rights.modelRestrictions
            ? html ` <strong>${dictionary(culture, 'filePreview', 'modelRestrictionsHeading')}</strong> <div class="rights-info"> ${dictionary(culture, 'filePreview', 'restrictionsApply')} </div> `
            : ``}
      ${rights.contactEmail
            ? html ` <strong>${dictionary(culture, 'filePreview', 'email')}</strong> <div class="rights-info">${rights.contactEmail}</div> `
            : ``}
      ${rights.contactPhone
            ? html ` <strong>${dictionary(culture, 'filePreview', 'phone')}</strong> <div class="rights-info">${rights.contactPhone}</div> `
            : ``}
      ${rights.contactCity
            ? html ` <strong>${dictionary(culture, 'filePreview', 'city')}</strong> <div class="rights-info">${rights.contactCity}</div> `
            : ``}
    `;
    }
    /**
     * Get color profile
     * @param {any} file - file
     * @returns {string} Marking info
     */
    async getColorProfile(file) {
        if (!(file.iccprofile && parseInt(file.iccprofile) == file.iccprofile)) {
            return '';
        }
        try {
            const response = await fetch(`${process.env.API_URL}/iccprofile/${file.iccprofile}?fields=any&access_token=${this.accessToken}`);
            if (response.status != 200) {
                return '';
            }
            const json = await response.json();
            if (json[0] && json[0].name != '') {
                return json[0].name;
            }
            return '';
        }
        catch (e) {
            console.error(e);
        }
    }
    /**
     * Get marking info
     * @param {number} marking - marking id
     * @returns {string} Marking info
     */
    async getMarking(marking) {
        try {
            const response = await fetch(`${process.env.API_URL}/mark?fields=any&access_token=${this.accessToken}`);
            const json = await response.json();
            const mark = json.find((mark) => mark.mark === marking);
            return mark;
        }
        catch (e) {
            console.error(e);
        }
    }
    /**
     * Close dialog
     * @returns {void}
     * */
    closeDialog() {
        this.innerHTML = '';
        this.isOpen = false;
        this.dialog.classList.remove('open');
        this.dialog.close();
    }
    handleKeyDown(e) {
        if (!this.dialog.isOpen()) {
            return;
        }
        // Arrow right
        if (e.key === 'ArrowRight' && this.currentIndex !== this.allFileIds.length) {
            this.open(this.allFileIds[this.nextIndex - 1], this.allFileIds);
        }
        // Arrow left
        if (e.key === 'ArrowLeft' && this.currentIndex > 1) {
            this.open(this.allFileIds[this.prevIndex - 1], this.allFileIds);
        }
    }
    async downloadFile() {
        // Check permissions
        if (!this.permissions?.allowDownload || !this.allowDownload) {
            return;
        }
        // Check usage reporting
        if (this.permissions?.accountDownloads || this.mustAccount) {
            // Open usage reporting dialog
            const usageReportDialog = document.querySelector('p-usage-report-dialog');
            if (usageReportDialog) {
                // TODO: Reset the form in the dialog
                usageReportDialog.fileId = this.file.id;
                usageReportDialog.filename = this.file.name;
                usageReportDialog.open();
            }
        }
        else {
            // Download file
            this.accessToken = window.getAccessToken();
            const response = await fetch(`${process.env.API_URL}/file/${this.file.id}/download/0`, {
                headers: {
                    Authorization: `Bearer ${this.accessToken}`
                }
            });
            const data = await response.json();
            if (response.status === 200) {
                const a = document.createElement('a');
                a.href = data[0].downloadURL;
                a.download = this.file.filename ? this.file.filename : 'file';
                a.click();
            }
            else {
                // Error?
            }
        }
    }
    /**
     * Open designgenerator
     * @returns {void}
     */
    async openDesignGenerator() {
        try {
            const response = await fetch(`${process.env.API_URL}/file/${this.file.id}/downloads?cache=0&access_token=${this.accessToken}`);
            const json = await response.json();
            if (json && json.length) {
                const downloadFormat = json.find((format) => {
                    return format.id === 0;
                });
                if (downloadFormat) {
                    window.open(downloadFormat.downloadURL, '_blank');
                }
            }
        }
        catch (e) {
            console.error(e);
        }
    }
    /**
     * Open embed code dialog
     * @returns {void}
     */
    openEmbedCodeDialog() {
        const shareDialog = document.querySelector('p-share-dialog');
        if (shareDialog) {
            shareDialog.mediaId = this.file.mediaId;
            shareDialog.mediaTitle = this.file.name;
            shareDialog.mediaWidth = this.file.width;
            shareDialog.mediaHeight = this.file.height;
            shareDialog.playerTheme = this.playerTheme;
            shareDialog.open();
        }
    }
    /**
     * Render component
     * @returns {TemplateResult} Template
     */
    render() {
        const classes = {
            'file-preview-loading': this.fileLoading,
            'preview-loading': this.previewLoading,
            ['view-mode-' + this.viewMode]: true
        };
        return html ` <p-dialog heading=${this.file?.name} dialog-id="file-preview" disable-body-scroll full-size hide-header @p-dialog-close=${this.closeDialog} > <p-button class="close-dialog" size="small" @p-click=${this.closeDialog} color="border" icon label=${dictionary(this.culture, 'dialog', 'close')} > <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-arrow-left" viewBox="0 0 16 16" > <path fill-rule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8" /> </svg> </p-button> <div class="file-preview${classMap(classes)}"> <div class="stage"> <div class="loader"></div> <div class="stage-inner"> ${this.file?.alpha && this.checkeredBackground ? html `<div class="checkered"></div>`
            : ''}
              <div class="image"></div>
              <div class="video"><slot name="video"></slot></div>
              ${this.viewMode == 'document'
            ? html ` <div class="document-preview"> <p-document-preview file-id=${this.file?.id}></p-document-preview> </div> `
            : ''}
            </div>
            ${this.allFileIds?.length > 1
            ? html ` <nav class="stepper" aria-label=${dictionary(this.culture, 'filePreview', 'stepper')} > <button ?disabled=${this.currentIndex == 1} @click=${() => this.open(this.allFileIds[this.prevIndex - 1], this.allFileIds)} class="prev" aria-label=${dictionary(this.culture, 'filePreview', 'previousFile')} > <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-chevron-left" viewBox="0 0 16 16" > <path fill-rule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0" /> </svg> </button> <div class="counter"> <span>${this.currentIndex}</span> of <span>${this.allFileIds.length}</span> </div> <button ?disabled=${this.currentIndex == this.allFileIds.length} class="next" aria-label=${dictionary(this.culture, 'filePreview', 'nextFile')} @click=${() => this.open(this.allFileIds[this.nextIndex - 1], this.allFileIds)} > <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-chevron-right" viewBox="0 0 16 16" > <path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708" /> </svg> </button> </nav> `
            : ''}
          </div>
          <div class="info">
            <div class="file-info">
              <h2>${this.file?.name}</h2>
              ${this.file &&
            this.file.description &&
            this.fields.findIndex((field) => field.id == 51) > -1
            ? html ` <p class="description">${this.file?.description}</p> `
            : ''}
              ${this.formattedFields.length > 0
            ? html ` <dl> ${this.formattedFields.map((field) => html `
                          <dt>
                            ${field.isCustomField
                ? field.title
                : field.id == 60
                    ? dictionary(this.culture, 'filePreview', 'rights')
                    : dictionary(this.culture, 'fields', `field-${field.id}`)}
                          </dt>
                          <dd>${field.value}</dd>
                        `)}
                    </dl>
                  `
            : ''}
            </div>

            <div class="actions">
              <div class="actions-inner">
                ${this.file?.type.type == 'video' && this.allowEmbed
            ? html ` <p-button @p-click=${this.openEmbedCodeDialog} block class="embed-code-button" color="border" > ${dictionary(this.culture, 'filePreview', 'embedCode')} </p-button> `
            : ''}
                ${this.file?.type.type == 'doctemplate'
            ? html ` <p-button block @p-click=${this.openDesignGenerator} >${dictionary(this.culture, 'filePreview', 'useTemplate')}</p-button > `
            : html ` ${this.permissions?.allowDownload && this.allowDownload ? html `
                            <p-button block @p-click=${this.downloadFile}
                              >${dictionary(this.culture, 'filePreview', 'download')}</p-button
                            >
                          `
                : ''}
                    `}
              </div>
            </div>
          </div>
        </div>
      </p-dialog>
    `;
    }
    /**
     * Check if file has a document preview
     * @param {number} fileTypeId - filetype id to check
     * @returns {boolean} hasDocumentPreview
     */
    hasDocumentPreview(fileTypeId) {
        return ((fileTypeId == 201 ||
            (fileTypeId >= 450 && fileTypeId < 460) ||
            (fileTypeId >= 500 && fileTypeId < 521) ||
            (fileTypeId >= 522 && fileTypeId < 580)) &&
            fileTypeId != 577 &&
            fileTypeId != 575);
    }
    /**
     * Get file icon path
     * @param {number} fileTypeId - filetype id
     * @returns {string} icon path
     */
    getFileIconPath(fileTypeId) {
        switch (fileTypeId) {
            // Image
            case 101:
                fileTypeId = 101;
                break;
            // Illustrator
            case 204:
                fileTypeId = 204;
                break;
            // Photoshop
            case 290:
                fileTypeId = 290;
                break;
            // MP4
            case 311:
                fileTypeId = 311;
                break;
            // Font
            case 401:
            case 402:
            case 410:
                fileTypeId = 401;
                break;
            // Presentation
            case 451:
            case 453:
            case 454:
            case 455:
            case 456:
                fileTypeId = 451;
                break;
            // Document
            case 501:
            case 502:
            case 504:
            case 505:
            case 506:
            case 507:
            case 570:
            case 575:
            case 576:
            case 577:
            case 591:
            case 801:
            case 802:
                fileTypeId = 501;
                break;
            // Spreadsheet
            case 520:
            case 521:
            case 522:
            case 523:
            case 571:
                fileTypeId = 520;
                break;
            // Indesign
            case 550:
            case 551:
            case 552:
                fileTypeId = 550;
                break;
            // Live
            case 585:
                fileTypeId = 585;
                break;
            // srt, vtt
            case 592:
            case 593:
                fileTypeId = 592;
                break;
            // Audio
            case 651:
            case 652:
            case 654:
            case 655:
            case 656:
            case 657:
            case 658:
            case 659:
            case 660:
            case 661:
            case 662:
            case 663:
                fileTypeId = 651;
                break;
            // Compressed file (zip, rar etc)
            case 701:
            case 702:
            case 703:
            case 704:
            case 705:
                fileTypeId = 701;
                break;
            default:
                fileTypeId = 999;
        }
        return `https://mfstatic.com/images/icons/filetype-${fileTypeId}-dark.svg`;
    }
    /**
     * Returns the field value
     * @param {id} id - Field id
     * @param {file} file - File object
     * @param {isCustomField} isCustomField - Is custom field
     * @returns {string} Formatted field value
     */
    async getFieldValue(id, file, isCustomField) {
        if (isCustomField) {
            // Get value from file.customFields where file.customFields.id == id
            const customField = file.customFields.find((field) => field.fieldId == id);
            if (customField) {
                if (customField.fieldType == 'DATE') {
                    return customField.value
                        ? new Date(customField.value).format(dictionary(this.culture, 'filePreview', 'dateFormat'), { showTime: true })
                        : '';
                }
                if ((customField.fieldType = 'MULTISELECT')) {
                    return customField.value ? customField.value.split(',').join(', ') : '';
                }
                return customField.value;
            }
        }
        switch (id) {
            case 1: // filename
                return file.filename;
            case 2:
                return file.type.extension; //TODO: make sure we should use extension, in the old we use description. But that just returnes "Quicktime-Video". It's better to return mp4 i think
            case 3: // name
                return '';
            case 4:
                return formatFileSize(file.filesize, this.culture);
            case 5: // print size
                if (!this.isImage(file.type.id)) {
                    return '';
                }
                const calcWidth = (file.width / file.dpi) * 25.4;
                const calcHeight = (file.height / file.dpi) * 25.4;
                return Math.round(calcWidth) + ' x ' + Math.round(calcHeight) + ' mm';
            case 6: // Length
                if (!file.length) {
                    return '';
                }
                if (file.type.type == 'video') {
                    return file.length.formatTime();
                }
                return file.length;
            case 7: // DPI
                return file.dpi;
            case 8: // Dimensions
                // Document or design template
                if ((file.type.id > 200 && file.type.id < 210) ||
                    file.type.extension == 'mfpd' ||
                    file.type.extension == 'mfdt') {
                    return `${file.width} x ${file.height} mm`;
                }
                // Videos
                if (file.width > 0 && file.type.type == 'video') {
                    let returnValue = file.width + ' x ' + file.height + ' px';
                    if (returnValue == '1920 x 1080 px')
                        returnValue = 'HD 1080p (1920 x 1080 px)';
                    if (returnValue == '1280 x 720 px')
                        returnValue = 'HD 720p (1280 x 720 px)';
                    if (returnValue == '3840 x 2160 px')
                        returnValue = '4k 2160p (3840 x 2160 px)';
                    return returnValue;
                }
                // Images / other
                if (file.width > 0) {
                    return file.width + ' x ' + file.height + ' px';
                }
                return '';
            case 9: // upload date TODO: should this be accessible from here
                return new Date(file.uploaded).format(dictionary(this.culture, 'filePreview', 'dateFormat'));
            case 50:
                return file.photoDate
                    ? new Date(file.photoDate).format(dictionary(this.culture, 'filePreview', 'dateFormat'))
                    : '';
            case 51: // description, skipped since its printed above fields
                return '';
            case 52: // instructions
                return file.instructions;
            case 53: // photographer
                return file.photographer;
            case 54: // additional info
                return file.additionalInfo;
            case 55: // rating
                return file.rating
                    ? html `<p-rating value=${file.rating} readonly culture=${this.culture} ?dark=${this.dark} ></p-rating>`
                    : '';
            case 56: // mark
                return file.mark
                    ? html `<div class="mark-label"> <p-mark value=${file.mark} culture=${this.culture}></p-mark> &nbsp; </div>`
                    : '';
            case 57: // geo data TODO: don't think this is aviable on videos, but you can pick the field to be visible on a playportal
                return file.geodata ? file.geodata?.latitude + ', ' + file.geodata?.longitude : '';
            case 58: // keywords
                return this.file.keywords.length
                    ? html `<div class="tags"> ${this.file.keywords?.map((keyword) => html `<p-tag>${keyword}</p-tag>`)}
            </div>`
                    : '';
            case 59: // TODO: Folders. shoudl we print these? They cant be linked to anything
                return '';
            // return html`${folders.map((folder:any, index:number) => html`<a href="${categoryBaseUrl}/${folder.id}">${folder.name}</a>${(index+1) != folders.length ? ', ' : ''}`)}`;
            case 60: // license, combine license and model to "rights"
                const licenseData = await this.getLicenseData(file.rights, this.culture);
                if (this.showModelInfo) {
                    const modelData = await this.getModelData(file.rights, this.culture);
                    if (licenseData || modelData) {
                        return html `${licenseData}${await this.getModelData(file.rights, this.culture)}`;
                    }
                    return '';
                }
                return licenseData;
            case 61: // model, only return if license isn't shared
                if (!this.showLicenseInfo) {
                    return await this.getModelData(file.rights, this.culture);
                }
                return '';
            case 62: // colour profile
                return await this.getColorProfile(file);
            case 63: // usage count
                return file.usageCount;
            case 64: // tag, AKA marking description
                return file.mark
                    ? html `<div class="mark-label"> <p-mark value=${file.mark} culture=${this.culture}></p-mark> ${(await this.getMarking(file.mark))?.name} </div>`
                    : '';
            default:
                return '';
        }
    }
    /**
     * Check if file is an image
     * @param {number} fileTypeId - file type id
     * @returns {boolean} isImage
     */
    isImage(fileTypeId) {
        return (fileTypeId > 100 && fileTypeId < 201) || (fileTypeId > 209 && fileTypeId < 300);
    }
};
__decorate([
    property({ type: String })
], PFilePreview.prototype, "culture", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'is-open' })
], PFilePreview.prototype, "isOpen", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'allow-download' })
], PFilePreview.prototype, "allowDownload", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'must-account' })
], PFilePreview.prototype, "mustAccount", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'allow-embed' })
], PFilePreview.prototype, "allowEmbed", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], PFilePreview.prototype, "dark", void 0);
__decorate([
    property({ type: String, attribute: 'player-theme' })
], PFilePreview.prototype, "playerTheme", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'checkered-background' })
], PFilePreview.prototype, "checkeredBackground", void 0);
__decorate([
    property()
], PFilePreview.prototype, "file", void 0);
__decorate([
    property({ type: Array })
], PFilePreview.prototype, "fields", void 0);
__decorate([
    property()
], PFilePreview.prototype, "allFileIds", void 0);
__decorate([
    property()
], PFilePreview.prototype, "currentIndex", void 0);
__decorate([
    property()
], PFilePreview.prototype, "nextIndex", void 0);
__decorate([
    property()
], PFilePreview.prototype, "prevIndex", void 0);
__decorate([
    query('p-dialog')
], PFilePreview.prototype, "dialog", void 0);
__decorate([
    query('.image')
], PFilePreview.prototype, "imageContainerElm", void 0);
__decorate([
    query('.file-preview')
], PFilePreview.prototype, "filePreviewElm", void 0);
__decorate([
    state()
], PFilePreview.prototype, "fileLoading", void 0);
__decorate([
    state()
], PFilePreview.prototype, "previewLoading", void 0);
__decorate([
    state()
], PFilePreview.prototype, "viewMode", void 0);
PFilePreview = __decorate([
    customElement('p-file-preview')
    /**
     * PFilePreview component
     *
     * @class PFilePreview
     * @typedef {PFilePreview}
     * @extends {LitElement}
     */
], PFilePreview);
export { PFilePreview };
